import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Hls from 'hls.js';
import type { ConversionJourney } from '@/store/videoFeatures/types/conversion-journey';
import { videoService } from '@/services/videoService';

/**
 * Overlay de PREVIEW (admin/QA) da Jornada de Conversão. Espelha o protótipo
 * (PrerollPlayerOverlay/ExitPlayerOverlay): renderiza um <video> próprio sobre
 * o player principal mostrando o vídeo de entrada (ou saída) com o botão de
 * pular e o card de CTA estilo YouTube. Resolve o HLS do vídeo referenciado
 * (libraryVideoId) via videoService.getVideoById e toca com hls.js (mesmo
 * caminho do VideoElement). É só preview — não dispara métrica nem sequência.
 */
function YouTubeCtaCard({
  title,
  description,
  ctaLabel,
  url,
  image,
}: {
  title: string;
  description?: string;
  ctaLabel: string;
  url?: string;
  image?: string;
}) {
  useLanguage();
  let host = '';
  try {
    if (url) host = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
  } catch {}
  const favicon = host ? `https://www.google.com/s2/favicons?domain=${host}&sz=64` : '';
  return (
    <div
      className="absolute bottom-3 left-3 flex items-center gap-2.5 rounded-[10px] bg-black/55 backdrop-blur-md p-2 pr-2.5 ring-1 ring-white/10 shadow-[0_4px_16px_rgba(0,0,0,0.35)]"
      style={{ maxWidth: 'calc(100% - 105px)' }}
    >
      <div className="w-9 h-9 rounded-md bg-[#F1F3F5] shrink-0 overflow-hidden flex items-center justify-center ring-1 ring-black/5">
        {image ? (
          <img src={image} alt="" className="w-full h-full object-cover" />
        ) : favicon ? (
          <img src={favicon} alt="" className="w-5 h-5 object-contain" />
        ) : (
          <div className="w-5 h-5 rounded-sm bg-gradient-to-br from-slate-300 to-slate-400" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold text-white leading-tight truncate">{title}</p>
        {description ? (
          <p className="text-[11px] text-white/70 leading-tight truncate">{description}</p>
        ) : null}
      </div>
      <button
        type="button"
        className="shrink-0 rounded-[6px] bg-white/90 hover:bg-white text-[#0F0F0F] text-[11.5px] font-semibold px-2.5 py-1.5 transition-colors"
      >
        {ctaLabel}
      </button>
    </div>
  );
}

interface Props {
  journey: ConversionJourney;
}

export const JourneyPreviewOverlay: React.FC<Props> = ({ journey }) => {
  useLanguage();
  const placement = journey.placementMode ?? 'before';
  // Em 'after' mostramos a saída; caso contrário, a entrada (preview da 1ª camada).
  const isExit = placement === 'after';
  const exit = journey.exit;

  const libraryVideoId = isExit ? exit?.libraryVideoId : journey.libraryVideoId;
  const libraryVideoThumbnail = isExit ? exit?.libraryVideoThumbnail : journey.libraryVideoThumbnail;
  const libraryVideoName = isExit ? exit?.libraryVideoName : journey.libraryVideoName;

  const ctaEnabled = isExit ? exit?.ctaEnabled : journey.ctaEnabled;
  const ctaTitle = isExit ? exit?.ctaTitle : journey.ctaTitle;
  const ctaDescription = isExit ? exit?.ctaDescription : journey.ctaDescription;
  const ctaText = isExit ? exit?.ctaText : journey.ctaText;
  const ctaUrl = isExit ? exit?.ctaUrl : journey.ctaUrl;
  const ctaImage = isExit ? exit?.ctaImage : journey.ctaImage;

  const skipEnabled = !isExit && journey.skipEnabled;
  const skipSeconds = journey.skipAfter === 'custom' ? journey.skipAfterCustomSec : journey.skipAfter;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [remaining, setRemaining] = useState<number>(Number(skipSeconds) || 0);
  // Quando a ENTRADA termina (ou é pulada) no preview, o overlay some e revela
  // o player principal por baixo — espelha o handoff entrada → principal.
  const [ended, setEnded] = useState(false);
  // Áudio: começa mudo (autoplay exige) e libera o som no 1º clique do admin —
  // espelha o unmute da jornada no player real (runner.js journeyArmAudioUnmute).
  const [audioOn, setAudioOn] = useState(false);

  // Resolve o HLS do vídeo referenciado via React Query: deduplica e cacheia
  // por id, então mesmo com várias montagens do overlay (StrictMode, layout que
  // duplica children, re-renders do VideoPlayer) a request HTTP acontece 1x.
  const { data: refData } = useQuery({
    queryKey: ['journey-ref-video', libraryVideoId],
    queryFn: () => videoService.getVideoById(String(libraryVideoId)),
    enabled: !!libraryVideoId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const streamUrl = useMemo(() => {
    const v = (refData as any)?.data ?? refData ?? {};
    return v.video || v.videoStreamUrl || v.videoMP4 || v.videoDownload || '';
  }, [refData]);

  // Reseta o handoff ao trocar o vídeo da camada (entrada/saída).
  useEffect(() => {
    setEnded(false);
    setAudioOn(false);
  }, [libraryVideoId]);

  // Anexa hls.js (ou nativo) ao <video> do overlay.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !streamUrl) return;
    const isHls = /\.m3u8(\?|$)/i.test(streamUrl);
    let hls: Hls | null = null;

    video.muted = true;
    if (!isHls || video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = streamUrl;
    } else if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true });
      hls.loadSource(streamUrl);
      hls.attachMedia(video);
    }
    const tryPlay = () => {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    tryPlay();
    video.addEventListener('canplay', tryPlay);
    return () => {
      video.removeEventListener('canplay', tryPlay);
      if (hls) {
        try {
          hls.destroy();
        } catch {}
      }
    };
  }, [streamUrl]);

  // Aplica o estado de áudio ao <video>. O unmute só vale após um gesto do
  // usuário (o clique no botão "Ativar som" satisfaz a política de autoplay).
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.muted = !audioOn;
  }, [audioOn, streamUrl]);

  // Countdown visual do botão de pular (somente preview).
  useEffect(() => {
    if (!skipEnabled) return;
    setRemaining(Number(skipSeconds) || 0);
    const id = setInterval(() => {
      setRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [skipEnabled, skipSeconds, streamUrl]);

  // Sem vídeo selecionado p/ esta camada → não cobre o player: mostra o
  // vídeo principal por baixo (overlay só aparece quando há vídeo escolhido).
  if (!libraryVideoId) return null;

  // Entrada terminada/pulada → libera o player principal (overlay some).
  if (ended && !isExit) return null;

  return (
    <div className="absolute inset-0 z-30 overflow-hidden rounded-[inherit]">
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)' }}
      />
      {streamUrl ? (
        <video
          ref={videoRef}
          poster={libraryVideoThumbnail || undefined}
          autoPlay
          muted={!audioOn}
          playsInline
          loop={isExit}
          preload="auto"
          onEnded={() => {
            // Só a entrada faz o handoff; a saída (loop) é estado final.
            if (!isExit) setEnded(true);
          }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : libraryVideoThumbnail ? (
        <>
          <img
            src={libraryVideoThumbnail}
            alt={libraryVideoName || ''}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
        </>
      ) : null}

      {streamUrl && !audioOn && (
        <button
          type="button"
          onClick={() => setAudioOn(true)}
          className="absolute top-3 left-3 z-40 inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur-md px-3 py-1.5 text-white text-[11px] font-semibold shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2a4.5 4.5 0 00-2.5-4.03v8.06A4.5 4.5 0 0016.5 12zM14 3.23v2.06a7 7 0 010 13.42v2.06a9 9 0 000-17.54z" />
          </svg>
          {translateInterfaceText("Ativar som")}</button>
      )}

      {skipEnabled && (
        <div
          onClick={() => {
            if (remaining <= 0) setEnded(true);
          }}
          className="absolute right-3 flex items-center gap-1.5 pl-3.5 pr-3 py-1.5 text-white text-[12.5px] font-medium rounded-full"
          style={{
            bottom: '22px',
            background: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(6px)',
            cursor: remaining <= 0 ? 'pointer' : 'default',
          }}
        >
          <span>{remaining > 0 ? translateInterfaceText("Pular em {value0}", { value0: (remaining) }) : journey.skipButtonText || translateInterfaceText("Pular")}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M6 4l12 8-12 8V4z" />
          </svg>
        </div>
      )}

      {ctaEnabled && (
        <YouTubeCtaCard
          title={ctaTitle || translateInterfaceText("Saiba mais")}
          description={ctaDescription}
          ctaLabel={ctaText || 'Saiba mais'}
          url={ctaUrl}
          image={ctaImage}
        />
      )}
    </div>
  );
};

export default JourneyPreviewOverlay;
